import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import type { Page } from '../../crm/client/pager'
import { ConfirmPopover } from '../ConfirmPopover'
import { useLibraryDeps } from '../LibraryDeps'
import { NewRow } from '../NewRow'
import { PanelSearch } from '../PanelSearch'
import { ListLoading } from '../ListLoading'
import { Sentinel } from '../Sentinel'
import { DENSITY_COL, useDensity } from '../useDensity'
import { useDebounced } from '../useDebounced'
import {
  MobileActionBar,
  MobileGroup,
  MobileListScreen,
  MobileOpenHead,
  MobileProgressBar,
  MobileSelectHead,
  SwitchCarousel,
  SwitchStack,
  type MobileAction,
  type SwitchItem,
} from '../mobile'
import { useCarousel } from '../useCarousel'
import { useGridSelect } from '../useGridSelect'
import { useMediaSrc } from '../useMediaSrc'
import { pageMemory } from '../pageMemory'
import { usePagedList } from '../usePagedList'
import { useSelection } from '../useSelection'
import { useToast } from '../useToast'
import {
  countsFor,
  fanLabel,
  filtersFor,
  toFan,
  toList,
  type Avatar,
  type Fan,
  type FanFilter,
  viewTarget,
  type FanList,
} from './collectionsData'
import { FanCard } from './FanCard'
import { FanPanel } from './FanPanel'
import { FanRow } from './FanRow'
import { ListMenu } from './ListMenu'
import { useFanJob, type FanJobKind } from './useFanJob'
import '../library.css'

/** Lists took seconds per 50 live; the old page used 20. */
const LIST_PAGE = 20
const FAN_PAGE = 24
/** How long the page fetched alongside the lists may stand in for opening that list. */
const EARLY_FRESH_MS = 60_000
/** Most fans one bulk change takes (decision 15). */
const BULK_CAP = 200

interface Props {
  accountId: string
  canWrite: boolean
  /** Open this fan's chat (the CRM chat, until chats are React). */
  onOpenChat: (fanId: number) => void
  /** The phone's bottom tab bar (the CRM pages), shown when not selecting. */
  mobileNav?: ReactNode
  /**
   * The page's URL query. `view=fans|following` opens that list,
   * `view=restricted|blocked` the Fans list on that filter (OnlyFans' Settings
   * → Privacy links).
   */
  query?: string
}

type Payload = { list?: Record<string, unknown>[]; hasMore?: boolean; nextOffset?: number }

const listId = (l: FanList) => l.id
const fanId = (f: Fan) => f.id

/** OnlyFans' own subscription lists page through /subscriptions/*, which take a status and a search. */
const SUBSCRIPTION_SOURCE: Record<string, { path: string; referer: string }> = {
  fans: { path: '/api2/v2/subscriptions/subscribers', referer: 'https://onlyfans.com/my/collections/user-lists/subscribers/' },
  following: { path: '/api2/v2/subscriptions/subscribes', referer: 'https://onlyfans.com/my/collections/user-lists/subscriptions/' },
}

/** Collections: the Account's OnlyFans fan lists and the fans in them (handoff-11 CRM Library). */
export function CollectionsPage({ accountId, canWrite, onOpenChat, mobileNav, query }: Props) {
  const { t } = useTranslation('library')
  const { gateway } = useLibraryDeps()
  const { say, node: toast } = useToast()
  const raw = useCallback(
    <T,>(apiPath: string, referer: string) => gateway<T>('/v1/of/raw', { method: 'GET', api_path: apiPath, referer, body: null }),
    [gateway],
  )

  // ── Lists ────────────────────────────────────────────────────────────────
  const [q, setQ] = useState('')
  const listQuery = useDebounced(q.trim())
  const fetchLists = useCallback(
    async (offset: number): Promise<Page<FanList>> => {
      const p = listQuery
        ? await raw<Payload>(
            `/api2/v2/lists?offset=${offset}&skip_users=dups&limit=${LIST_PAGE}&format=infinite&query=${encodeURIComponent(listQuery)}`,
            'https://onlyfans.com/my/collections/user-lists',
          )
        : await gateway<Payload>('/v1/lists/get', { offset, limit: LIST_PAGE, skip_users: 'dups' })
      return { list: (p.list ?? []).map(toList), hasMore: p.hasMore, nextOffset: p.nextOffset }
    },
    // accountId: another account has other lists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gateway, raw, accountId, listQuery],
  )
  const lists = usePagedList(fetchLists, listId, listQuery ? null : `of-col-lists:${accountId}`)

  const [openId, setOpenIdRaw] = useState<string | null>(null)
  // The open list stays on screen while a search hides its row.
  const [openSnapshot, setOpenSnapshot] = useState<FanList | null>(null)
  const open = lists.items.find((l) => l.id === openId) ?? (openSnapshot?.id === openId ? openSnapshot : null)
  const [filter, setFilter] = useState<FanFilter>('all')
  const [fq, setFq] = useState('')
  const fanQuery = useDebounced(fq.trim())
  const job = useFanJob()
  const [menu, setMenu] = useState<'add' | 'remove' | 'list' | 'clear' | 'delete' | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [creating, setCreating] = useState(false)
  const [panelId, setPanelId] = useState<number | null>(null)
  // Phone: 0 = the lists, 1 = one list; Select mode can be on with nothing picked.
  const mobile = useIsNarrow()
  const [level, setLevel] = useState<0 | 1>(0)
  const [selectMode, setSelectMode] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const openList = useCallback(
    (l: FanList) => {
      setOpenIdRaw(l.id)
      setOpenSnapshot(l)
      pageMemory.openList.set(accountId, l.id)
      setFilter('all')
      setFq('')
      setMenu(null)
      setRenaming(false)
      setPanelId(null)
      setSelectMode(false)
      if (scrollRef.current) scrollRef.current.scrollTop = 0
      job.reset()
    },
    [job, accountId],
  )

  const target = useMemo(() => viewTarget(query), [query])
  /** The asked list, else the one last open here: it opens first when the lists have it. */
  const [remembered] = useState(() => target?.list ?? pageMemory.openList.get(accountId) ?? null)
  /** The list most likely to open first (OnlyFans lists Fans first), fetched before the lists land. */
  const firstGuess = remembered ?? 'fans'
  useEffect(() => {
    if (openId !== null || listQuery || !lists.items[0]) return
    const wanted = target && lists.items.find((l) => l.id === target.list)
    const before = remembered ? lists.items.find((l) => l.id === remembered) : undefined
    openList(wanted ?? before ?? lists.items[0])
    if (wanted && target) setFilter(target.filter)
  }, [openId, listQuery, lists.items, openList, target, remembered])

  const updateList = (id: string, change: (l: FanList) => FanList) => {
    lists.setItems((all) => all.map((l) => (l.id === id ? change(l) : l)))
    setOpenSnapshot((s) => (s?.id === id ? change(s) : s))
  }

  // ── Status counts (Fans / Following) ─────────────────────────────────────
  const filters = filtersFor(open)
  const [countPayload, setCountPayload] = useState<Record<string, unknown> | null>(() => pageMemory.subscriptionCounts.get(accountId) ?? null)
  // Asked on mount, alongside the lists, when the list opening first is Fans or Following.
  const [countsAsked, setCountsAsked] = useState(false)
  const wantCounts = filters.length > 0 || (openId === null && Boolean(SUBSCRIPTION_SOURCE[firstGuess]))
  useEffect(() => {
    if (!wantCounts || countsAsked) return
    setCountsAsked(true)
    gateway<Record<string, unknown>>('/v1/subscriptions/count', {})
      .then((p) => {
        pageMemory.subscriptionCounts.set(accountId, p ?? {})
        setCountPayload(p ?? {})
      })
      .catch(() => setCountPayload((c) => c ?? {}))
  }, [wantCounts, countsAsked, gateway, accountId])
  const counts = countsFor(open, countPayload)

  // ── Fans of the open list ────────────────────────────────────────────────
  const loadFans = useCallback(
    async (id: string, status: FanFilter, search: string, offset: number): Promise<Page<Fan>> => {
      const source = SUBSCRIPTION_SOURCE[id]
      const p = source
        ? await raw<Payload>(
            `${source.path}?offset=${offset}&limit=${FAN_PAGE}&type=${status}&format=infinite${search ? `&query=${encodeURIComponent(search)}` : ''}`,
            source.referer + status,
          )
        : await gateway<Payload>('/v1/lists/get-users', {
            list_id: id,
            offset,
            limit: FAN_PAGE,
            ...(search ? { query: search } : {}),
          })
      return { list: (p.list ?? []).map(toFan), hasMore: p.hasMore, nextOffset: p.nextOffset }
    },
    [gateway, raw],
  )
  // The first page of the list that will most likely open, asked together with
  // the lists instead of after them; used only if that list does open.
  const early = useRef<{ key: string; page: Promise<Page<Fan>>; at: number } | null>(null)
  useEffect(() => {
    const key = `${firstGuess}|${target?.filter ?? 'all'}`
    const page = loadFans(firstGuess, target?.filter ?? 'all', '', 0)
    page.catch(() => undefined)
    early.current = { key, page, at: Date.now() }
    // Mount only: later opens fetch as usual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const fetchFans = useCallback(
    async (offset: number): Promise<Page<Fan>> => {
      if (!openId) return { list: [], hasMore: false }
      const head = early.current
      if (offset === 0 && !fanQuery && head?.key === `${openId}|${filter}` && Date.now() - head.at < EARLY_FRESH_MS) {
        early.current = null
        return head.page
      }
      return loadFans(openId, filter, fanQuery, offset)
    },
    [loadFans, openId, filter, fanQuery],
  )
  const fans = usePagedList(fetchFans, fanId, openId && !fanQuery ? `of-col-fans:${accountId}:${openId}:${filter}` : null)
  const items = fans.items

  // ── Selection ────────────────────────────────────────────────────────────
  const keys = useMemo(() => items.map((f) => f.id), [items])
  const sel = useSelection(keys, canWrite)
  const picked = sel.picked
  const selecting = canWrite && (selectMode || picked.length > 0)
  const drag = useGridSelect({ scrollRef, keys, picked, onPick: sel.replace, enabled: canWrite, selecting: mobile ? selecting : undefined })
  const carousel = useCarousel(mobile && level === 1 && !selecting)
  const [density, setDensity] = useDensity()
  void setDensity

  // ── Bulk add / remove (one OnlyFans call per fan and list) ───────────────
  const [lastBulk, setLastBulk] = useState<{ kind: FanJobKind; lists: FanList[] } | null>(null)

  const isIn = (f: Fan, listId: string) => f.lists.some((s) => s.id === listId && s.hasUser)
  const joined = (f: Fan, added: FanList[]): Fan => ({
    ...f,
    lists: [
      ...f.lists.filter((s) => !added.some((l) => l.id === s.id)),
      ...added.map((l) => ({ id: l.id, name: l.name, hasUser: true, canAddUser: false })),
    ],
  })
  const listsLabel = (ls: FanList[]) => (ls.length === 1 ? ls[0]!.name : t('collections.nLists', { count: ls.length }))

  /** Adds to every list in `lists`, or removes from the one list in it. */
  const runBulk = async (kind: FanJobKind, lists: FanList[], ids: number[]) => {
    setMenu(null)
    setLastBulk({ kind, lists })
    const path = kind === 'add' ? '/v1/lists/add-fan' : '/v1/lists/remove-fan'
    const before = new Map(items.map((f) => [f.id, f]))
    // What went through per fan, so a fan retried after a rate limit (or a
    // partly failed one) never repeats a call that already worked.
    const changed = new Map<number, FanList[]>()
    const result = await job.start({
      kind,
      listName: listsLabel(lists),
      fanIds: ids,
      run: async (id) => {
        const fan = before.get(id)
        for (const list of lists) {
          if (changed.get(id)?.includes(list) || (kind === 'add' && fan && isIn(fan, list.id))) continue
          await gateway(path, { list_id: list.id, fan_id: String(id) })
          changed.set(id, [...(changed.get(id) ?? []), list])
        }
      },
    })
    const done = result.done.map(Number)
    const failed = result.failed.map((f) => Number(f.id))
    if (kind === 'add') {
      fans.setItems((all) => all.map((f) => (changed.has(f.id) ? joined(f, changed.get(f.id)!) : f)))
      for (const list of lists) {
        const n = [...changed.values()].filter((got) => got.includes(list)).length
        if (n) updateList(list.id, (l) => ({ ...l, users: l.users + n }))
      }
    } else {
      const list = lists[0]!
      const gone = [...changed.keys()]
      if (list.id === openId) fans.setItems((all) => all.filter((f) => !gone.includes(f.id)))
      updateList(list.id, (l) => ({ ...l, users: Math.max(0, l.users - gone.length) }))
    }
    sel.replace(failed)
    setSelectMode(false)
    const parts: string[] = []
    if (done.length) parts.push(t(kind === 'add' ? 'collections.added' : 'collections.removed', { count: done.length, name: listsLabel(lists) }))
    if (failed.length) parts.push(t('collections.failed', { count: failed.length }))
    if (result.cancelled) parts.push(t('collections.stopped'))
    if (parts.length) say(parts.join(' · '))
  }

  const pickedFans = items.filter((f) => picked.includes(f.id))

  const addPicked = (lists: FanList[]) => {
    const todo = pickedFans.filter((f) => lists.some((l) => !isIn(f, l.id))).map((f) => f.id)
    if (!todo.length) {
      setMenu(null)
      say(t('collections.allAlreadyIn', { name: listsLabel(lists) }))
      return
    }
    void runBulk('add', lists, todo)
  }

  const retry = () => {
    if (lastBulk && job.failedIds.length) void runBulk(lastBulk.kind, lastBulk.lists, job.failedIds)
  }

  // ── One fan (panel) ──────────────────────────────────────────────────────
  const panelFan = items.find((f) => f.id === panelId) ?? null
  const fail = (e: unknown) => say(e instanceof Error ? e.message : t('error'))

  const addOne = async (fan: Fan, lists: FanList[]) => {
    const got: FanList[] = []
    try {
      for (const list of lists) {
        if (isIn(fan, list.id)) continue
        await gateway('/v1/lists/add-fan', { list_id: list.id, fan_id: String(fan.id) })
        got.push(list)
      }
      say(t('collections.added', { count: 1, name: listsLabel(lists) }))
    } catch (e) {
      fail(e)
    }
    if (!got.length) return
    fans.setItems((all) => all.map((f) => (f.id === fan.id ? joined(f, got) : f)))
    for (const list of got) updateList(list.id, (l) => ({ ...l, users: l.users + 1 }))
  }

  const removeOne = async (fan: Fan, id: string) => {
    const name = fan.lists.find((s) => s.id === id)?.name ?? ''
    try {
      await gateway('/v1/lists/remove-fan', { list_id: id, fan_id: String(fan.id) })
      fans.setItems((all) =>
        id === openId
          ? all.filter((f) => f.id !== fan.id)
          : all.map((f) => (f.id === fan.id ? { ...f, lists: f.lists.map((s) => (s.id === id ? { ...s, hasUser: false } : s)) } : f)),
      )
      updateList(id, (l) => ({ ...l, users: Math.max(0, l.users - 1) }))
      say(t('collections.removed', { count: 1, name }))
    } catch (e) {
      fail(e)
    }
  }

  const restrict = async (fan: Fan) => {
    try {
      await gateway('/v1/users/restrict', { user_id: String(fan.id) })
      fans.setItems((all) => all.map((f) => (f.id === fan.id ? { ...f, restricted: true } : f)))
      say(t('collections.restricted', { name: fanLabel(fan) }))
    } catch (e) {
      fail(e)
    }
  }

  const copyLink = (fan: Fan) => {
    void navigator.clipboard?.writeText(`https://onlyfans.com/${fan.username}`).then(
      () => say(t('collections.linkCopied')),
      () => say(t('error')),
    )
  }

  // ── List management ──────────────────────────────────────────────────────
  const createList = async (name: string) => {
    try {
      const created = toList({ ...(await gateway<Record<string, unknown>>('/v1/lists/create', { list_name: name })), name })
      lists.setItems((all) => [created, ...all])
      openList(created)
    } catch (e) {
      fail(e)
      throw e
    }
  }

  const rename = async (name: string) => {
    if (!open) return
    const id = open.id
    setRenaming(false)
    if (!name || name === open.name) return
    try {
      await gateway('/v1/lists/rename', { list_id: id, name })
      updateList(id, (l) => ({ ...l, name }))
    } catch (e) {
      fail(e)
    }
  }

  const clearList = async () => {
    if (!open) return
    const l = open
    setMenu(null)
    try {
      await gateway('/v1/lists/clear', { list_id: l.id })
      fans.setItems([])
      updateList(l.id, (x) => ({ ...x, users: 0, previews: [] }))
      say(t('collections.cleared', { name: l.name }))
    } catch (e) {
      fail(e)
    }
  }

  const deleteList = async () => {
    if (!open) return
    const l = open
    setMenu(null)
    try {
      await gateway('/v1/lists/delete', { list_id: l.id })
      const rest = lists.items.filter((x) => x.id !== l.id)
      lists.setItems(rest)
      say(t('collections.deleted', { name: l.name }))
      if (rest[0]) openList(rest[0])
      else setOpenIdRaw(null)
    } catch (e) {
      fail(e)
    }
  }

  const listMenu = open && (
    <>
      {menu === 'list' && (
        <>
          <div className="lib-backdrop" onClick={() => setMenu(null)} />
          <div className="lib-pop lib-menu lib-menu--right" role="menu" aria-label={t('collections.listActions')}>
            {open.canUpdate && (
              <button type="button" role="menuitem" className="lib-menu-item" onClick={() => (setMenu(null), setRenaming(true))}>
                <span className="lib-menu-label">{t('collections.rename')}</span>
              </button>
            )}
            {open.canUpdate && open.canManageUsers && (
              <button type="button" role="menuitem" className="lib-menu-item" onClick={() => setMenu('clear')}>
                <span className="lib-menu-label">{t('collections.clearList')}</span>
              </button>
            )}
            {open.canDelete && (
              <button type="button" role="menuitem" className="lib-menu-item lib-menu-item--danger" onClick={() => setMenu('delete')}>
                <span className="lib-menu-label">{t('collections.deleteList')}</span>
              </button>
            )}
          </div>
        </>
      )}
      {menu === 'clear' && (
        <ConfirmPopover
          title={t('collections.clearTitle', { name: open.name })}
          body={t('collections.clearBody')}
          confirmLabel={t('collections.clearList')}
          onConfirm={clearList}
          onCancel={() => setMenu(null)}
        />
      )}
      {menu === 'delete' && (
        <ConfirmPopover
          title={t('collections.deleteTitle', { name: open.name })}
          body={t('collections.deleteBody')}
          confirmLabel={t('collections.deleteList')}
          onConfirm={deleteList}
          onCancel={() => setMenu(null)}
        />
      )}
    </>
  )

  const addMenu = (
    <ListMenu
      title={t('collections.addTitle', { count: picked.length })}
      lists={lists.items}
      fans={pickedFans}
      exceptId={openId ?? undefined}
      onAdd={addPicked}
      onClose={() => setMenu(null)}
    />
  )
  const removeConfirm = open && (
    <ConfirmPopover
      title={t('collections.removeTitle', { count: picked.length, name: open.name })}
      body={t('collections.removeBody')}
      confirmLabel={t('collections.remove')}
      onConfirm={() => void runBulk('remove', [open], [...picked])}
      onCancel={() => setMenu(null)}
    />
  )

  const chips = filters.length > 0 && (
    <div className="lib-chips" role="radiogroup" aria-label={t('collections.statusFilter')}>
      {filters.map((f) => {
        const on = filter === f
        const n = counts[f]
        return (
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={on}
            className={`lib-chip ${on ? 'is-on' : ''}`}
            onClick={() => {
              setFilter(f)
              job.reset()
            }}
          >
            {t(`collections.chip.${f}`)}
            {n !== undefined && <span className="lib-chip-n">{n.toLocaleString()}</span>}
          </button>
        )
      })}
    </div>
  )

  const canManage = canWrite && !!open?.canManageUsers
  const hasListActions = canWrite && !!open && (open.canUpdate || open.canDelete)
  const tooMany = picked.length > BULK_CAP
  const title = open?.name ?? ''
  const noFans = !fans.loading && !fans.hasMore && items.length === 0
  const emptyText = fanQuery || filter !== 'all' ? t('collections.noMatch') : t('collections.empty')
  const panel = panelFan && (
    <FanPanel
      fan={panelFan}
      lists={lists.items}
      canWrite={canWrite}
      onClose={() => setPanelId(null)}
      onMessage={() => onOpenChat(panelFan.id)}
      onAddTo={(ls) => void addOne(panelFan, ls)}
      onRemoveFrom={(id) => void removeOne(panelFan, id)}
      onRestrict={() => void restrict(panelFan)}
      onCopyLink={() => copyLink(panelFan)}
    />
  )

  if (mobile) {
    const switchItems: SwitchItem[] = lists.items.map((l) => ({
      id: l.id,
      label: l.name,
      on: l.id === openId,
      thumb: { round: true, url: l.previews[0]?.url, initial: (l.previews[0]?.name || l.name).slice(0, 1).toUpperCase() },
    }))
    const switchTo = (id: string) => {
      const l = lists.items.find((x) => x.id === id)
      carousel.setOpen(false)
      sel.clear()
      if (l && l.id !== openId) openList(l)
    }
    const endSelect = () => {
      setSelectMode(false)
      sel.clear()
      setMenu(null)
    }
    const tooManyTitle = tooMany ? t('collections.tooMany', { n: BULK_CAP }) : undefined
    const actions: MobileAction[] = [
      { icon: 'user-plus', label: t('collections.addTo'), disabled: !picked.length || tooMany, title: tooManyTitle, onPress: () => setMenu('add') },
    ]
    if (canManage && open) {
      actions.push({ icon: 'user-minus', label: t('collections.remove'), disabled: !picked.length || tooMany, title: tooManyTitle, onPress: () => setMenu('remove') })
    }
    if (job.failedIds.length > 0 && lastBulk) actions.push({ icon: 'rotate-cw', label: t('collections.retry'), onPress: retry })

    return (
      <div className="lib lib--m">
        {level === 0 ? (
          <MobileListScreen
            title={t('collections.title')}
            addLabel={t('collections.newList')}
            onAdd={canWrite ? () => setCreating(true) : undefined}
            searchLabel={t('collections.searchLists')}
            query={q}
            onQuery={setQ}
          >
            {creating && <NewRow placeholder={t('collections.listName')} onCreate={createList} onDone={() => setCreating(false)} />}
            <MobileGroup label={t('collections.lists')} count={lists.items.length} />
            {lists.items.map((l) => (
              <ListRow
                key={l.id}
                list={l}
                on={false}
                mobile
                onOpen={() => {
                  if (l.id !== openId) openList(l)
                  setLevel(1)
                }}
              />
            ))}
            <ListLoading loading={lists.loading} empty={!lists.items.length} />
            {lists.hasMore && <Sentinel onVisible={lists.loadMore} token={lists.items.length} />}
            {!lists.loading && !lists.hasMore && !lists.items.length && <p className="lib-empty">{t('mobile.nothingMatches')}</p>}
          </MobileListScreen>
        ) : (
          <>
            {selecting ? (
              <MobileSelectHead count={picked.length} onCancel={endSelect} onSelectAll={sel.selectAll} />
            ) : (
              <MobileOpenHead
                title={renaming && open ? <RenameInput label={t('collections.listName')} initial={open.name} onDone={rename} /> : title}
                sub={open ? t('collections.users', { count: open.users, n: open.users.toLocaleString() }) : ''}
                onBack={() => {
                  endSelect()
                  carousel.setOpen(false)
                  setPanelId(null)
                  setLevel(0)
                }}
                switcher={<SwitchStack items={switchItems} open={carousel.open} onToggle={carousel.toggle} label={t('mobile.switchList')} />}
                onSelect={canWrite ? () => setSelectMode(true) : undefined}
                more={
                  hasListActions && (
                    <button type="button" className="lib-mbtn" aria-label={t('collections.listActions')} onClick={() => setMenu(menu === 'list' ? null : 'list')}>
                      <FeatherIcon name="more-vertical" size={20} />
                    </button>
                  )
                }
              />
            )}
            <SwitchCarousel items={switchItems} open={carousel.open} onPick={switchTo} label={t('mobile.switchList')} />
            {chips && <div className="lib-mchips">{chips}</div>}
            <div
              ref={scrollRef}
              className="lib-scroll lib-scroll--m"
              {...drag.rootProps}
              {...carousel.scrollProps}
              style={drag.rootProps.style}
            >
              <div className="lib-msearch lib-msearch--inline">
                <PanelSearch label={t('collections.searchFans')} value={fq} onChange={setFq} />
              </div>
              <div className="lib-mlist" role="listbox" aria-label={title} aria-multiselectable="true">
                {items.map((f) => (
                  <FanRow
                    key={f.id}
                    fan={f}
                    selected={picked.includes(f.id)}
                    onToggle={canWrite ? () => sel.toggle(f.id) : undefined}
                    onOpen={() => (selecting ? sel.toggle(f.id) : setPanelId(f.id))}
                    onMessage={() => onOpenChat(f.id)}
                    job={job.cards[f.id]}
                  />
                ))}
              </div>
              {noFans && <p className="lib-empty">{emptyText}</p>}
              <ListLoading loading={fans.loading} empty={!items.length} />
              {fans.hasMore && <Sentinel onVisible={fans.loadMore} token={items.length} />}
            </div>
          </>
        )}
        {level === 1 && job.job ? (
          <MobileProgressBar
            text={t(job.job.kind === 'add' ? 'collections.progressAdd' : 'collections.progressRemove', {
              done: job.job.finished,
              total: job.job.total,
              name: job.job.listName,
            })}
            done={job.job.finished}
            total={job.job.total}
            onCancel={job.cancel}
          />
        ) : level === 1 && selecting ? (
          <MobileActionBar actions={actions} label={t('mobile.actions')} />
        ) : (
          mobileNav
        )}
        {level === 1 && listMenu}
        {menu === 'add' && addMenu}
        {menu === 'remove' && removeConfirm}
        {panel}
        {toast}
      </div>
    )
  }

  return (
    <div className="lib">
      <aside className="lib-panel">
        <div className="lib-panel-head">
          <h1 className="lib-panel-title">{t('collections.title')}</h1>
          {canWrite && (
            <button type="button" className="lib-icon-btn" aria-label={t('collections.newList')} title={t('collections.newList')} onClick={() => setCreating(true)}>
              <FeatherIcon name="plus" size={18} />
            </button>
          )}
        </div>
        <PanelSearch label={t('collections.searchLists')} value={q} onChange={setQ} />
        {creating && <NewRow placeholder={t('collections.listName')} onCreate={createList} onDone={() => setCreating(false)} />}
        <nav className="lib-rows" aria-label={t('collections.lists')}>
          <div className="lib-group">
            <span>{t('collections.lists')}</span>
            <span>{lists.items.length}</span>
          </div>
          {lists.items.map((l) => (
            <ListRow key={l.id} list={l} on={l.id === openId} onOpen={() => openList(l)} />
          ))}
          <ListLoading loading={lists.loading} empty={!lists.items.length} />
          {lists.hasMore && <Sentinel onVisible={lists.loadMore} token={lists.items.length} />}
        </nav>
      </aside>
      <main className="lib-main">
        <header className="lib-main-head">
          {renaming && open ? (
            <RenameInput label={t('collections.listName')} initial={open.name} onDone={rename} />
          ) : (
            <h2 className="lib-main-title">{title}</h2>
          )}
          {open && <span className="lib-main-sub">{t('collections.users', { count: open.users, n: open.users.toLocaleString() })}</span>}
          <span className="lib-spacer" />
          {hasListActions && open && (
            <div className="lib-anchor">
              <button type="button" className="lib-icon-btn" aria-label={t('collections.listActions')} onClick={() => setMenu(menu === 'list' ? null : 'list')}>
                <FeatherIcon name="more-horizontal" size={18} />
              </button>
              {listMenu}
            </div>
          )}
        </header>
        <div className="lib-toolbar">
          {chips}
          <div className="lib-toolbar-search">
            <PanelSearch label={t('collections.searchFans')} value={fq} onChange={setFq} />
          </div>
          <span className="lib-spacer" />
          {job.job ? (
            <div className="lib-selbar" role="group" aria-label={t('collections.progressLabel')}>
              <span className="lib-selbar-n">
                {t(job.job.kind === 'add' ? 'collections.progressAdd' : 'collections.progressRemove', {
                  done: job.job.finished,
                  total: job.job.total,
                  name: job.job.listName,
                })}
              </span>
              <span className="lib-progress" aria-hidden="true">
                <span style={{ width: `${(job.job.finished / Math.max(1, job.job.total)) * 100}%` }} />
              </span>
              <button type="button" className="lib-btn lib-btn--ghost" onClick={job.cancel}>
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            picked.length > 0 && (
              <div className="lib-selbar">
                <span className="lib-selbar-n">{t('selected', { count: picked.length })}</span>
                {job.failedIds.length > 0 && lastBulk && (
                  <button type="button" className="lib-btn" onClick={retry}>
                    {t('collections.retry')}
                  </button>
                )}
                <div className="lib-anchor">
                  <button
                    type="button"
                    className="lib-btn"
                    disabled={tooMany}
                    title={tooMany ? t('collections.tooMany', { n: BULK_CAP }) : undefined}
                    onClick={() => setMenu(menu === 'add' ? null : 'add')}
                  >
                    {t('collections.addTo')}
                  </button>
                  {menu === 'add' && addMenu}
                </div>
                {canManage && open && (
                  <div className="lib-anchor">
                    <button
                      type="button"
                      className="lib-btn"
                      disabled={tooMany}
                      title={tooMany ? t('collections.tooMany', { n: BULK_CAP }) : undefined}
                      onClick={() => setMenu(menu === 'remove' ? null : 'remove')}
                    >
                      {t('collections.removeFrom')}
                    </button>
                    {menu === 'remove' && removeConfirm}
                  </div>
                )}
                <span className="lib-divider" />
                <button type="button" className="lib-btn lib-btn--ghost" onClick={sel.selectAll}>
                  {t('selectAllShown')}
                </button>
                <button type="button" className="lib-btn lib-btn--ghost" onClick={() => (sel.clear(), setMenu(null))}>
                  {t('clear')}
                </button>
              </div>
            )
          )}
        </div>
        <div ref={scrollRef} className="lib-scroll" {...drag.rootProps} style={{ ['--lib-col' as string]: DENSITY_COL[density], ...drag.rootProps.style }}>
          {drag.box && <div className="lib-dragbox" style={{ left: drag.box.left, top: drag.box.top, width: drag.box.width, height: drag.box.height }} />}
          <div className="lib-grid lib-grid--fans" role="listbox" aria-label={title} aria-multiselectable="true">
            {items.map((f) => (
              <FanCard
                key={f.id}
                fan={f}
                selected={picked.includes(f.id)}
                onToggle={canWrite ? () => sel.toggle(f.id) : undefined}
                onOpen={(e) => sel.clickWithKeys(f.id, e) || setPanelId(f.id)}
                onMessage={() => onOpenChat(f.id)}
                job={job.cards[f.id]}
              />
            ))}
          </div>
          {noFans && <p className="lib-empty">{emptyText}</p>}
          <ListLoading loading={fans.loading} empty={!items.length} />
          {fans.hasMore && <Sentinel onVisible={fans.loadMore} token={items.length} />}
        </div>
      </main>
      {panel}
      {toast}
    </div>
  )
}

function ListRow({ list, on, onOpen, mobile = false }: { list: FanList; on: boolean; onOpen: () => void; mobile?: boolean }) {
  const { t } = useTranslation('library')
  return (
    <button
      type="button"
      className={`lib-row ${mobile ? 'lib-row--m' : ''} ${on ? 'is-on' : ''}`}
      aria-current={on ? 'true' : undefined}
      onClick={onOpen}
    >
      <span className="lib-row-text">
        <span className="lib-row-label">{list.name}</span>
        <span className="lib-row-meta">
          {list.users
            ? [
                t('collections.users', { count: list.users, n: list.users.toLocaleString() }),
                list.posts ? t('collections.posts', { count: list.posts, n: list.posts.toLocaleString() }) : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : t('vault.empty')}
        </span>
      </span>
      <span className="lib-row-prev" aria-hidden="true">
        {list.previews.map((a) => (
          <ListAvatar key={a.id} avatar={a} />
        ))}
      </span>
      {mobile && <FeatherIcon name="chevron-right" size={18} className="lib-row-chev" />}
    </button>
  )
}

function ListAvatar({ avatar }: { avatar: Avatar }) {
  const { ref, src } = useMediaSrc(avatar.url)
  return (
    <span ref={ref as React.RefObject<HTMLSpanElement>} className="lib-prev lib-prev--round">
      {src ? <img src={src} alt="" /> : avatar.name.slice(0, 1).toUpperCase()}
    </span>
  )
}

function RenameInput({ label, initial, onDone }: { label: string; initial: string; onDone: (name: string) => void }) {
  const [value, setValue] = useState(initial)
  return (
    <input
      className="lib-rename"
      aria-label={label}
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onDone(value.trim())
        if (e.key === 'Escape') onDone(initial)
      }}
      onBlur={() => onDone(initial)}
    />
  )
}
